import { PasswordInputRow } from './PasswordInputRow';
import { AuthOtpCodeField } from './AuthOtpCodeField';
import { usePasswordReset } from '../login/usePasswordReset';
import type { LoginOption } from '../login/loginConstants';
import { useI18n } from '../i18n';
import styles from '../pages/AuthLoginPortal.module.css';

export type AuthForgotPasswordSectionProps = {
  loginType: LoginOption;
  onBackToSignIn: () => void;
};

export function AuthForgotPasswordSection({ loginType, onBackToSignIn }: AuthForgotPasswordSectionProps) {
  const { t } = useI18n();
  const reset = usePasswordReset(loginType);
  const inCodeStep = reset.step === 'code';
  const inPasswordStep = reset.step === 'password';
  const done = reset.step === 'done';

  const hint = done
    ? t('auth.forgotPassword.success')
    : inPasswordStep
      ? t('auth.forgotPassword.setPasswordHint')
      : reset.channel === 'email'
        ? inCodeStep && reset.codeSent
          ? t('auth.forgotPassword.codeSentEmail', { email: reset.email })
          : t('auth.forgotPassword.emailHint')
        : inCodeStep && reset.codeSent
          ? t('auth.forgotPassword.codeSentPhone', { phone: reset.displayPhone })
          : t('auth.forgotPassword.phoneHint');

  const submitLabel = inPasswordStep
    ? t('auth.forgotPassword.submitNewPassword')
    : inCodeStep
      ? t('auth.forgotPassword.verifyCode')
      : t('auth.portal.sendCode');

  return (
    <section aria-labelledby="auth-forgot-password-hint">
      {reset.phoneOtpEnabled && reset.step === 'identifier' ? (
        <div className={styles.otpChannelTabs} role="tablist" aria-label={t('auth.forgotPassword.title')}>
          <button
            type="button"
            role="tab"
            className={styles.otpChannelTab}
            aria-selected={reset.channel === 'phone'}
            onClick={() => reset.onChangeChannel('phone')}
          >
            {t('auth.portal.phoneOtpTitle')}
          </button>
          <button
            type="button"
            role="tab"
            className={styles.otpChannelTab}
            aria-selected={reset.channel === 'email'}
            onClick={() => reset.onChangeChannel('email')}
          >
            {t('auth.portal.emailOtpTitle')}
          </button>
        </div>
      ) : null}

      <p
        id="auth-forgot-password-hint"
        className={done ? styles.successHint : styles.hint}
        role={done ? 'status' : undefined}
      >
        {hint}
      </p>

      {done ? (
        <button type="button" className={styles.primaryBtn} onClick={onBackToSignIn}>
          {t('auth.forgotPassword.backToSignIn')}
        </button>
      ) : (
        <form className={styles.otpForm} onSubmit={reset.onSubmit}>
          {reset.channel === 'email' && reset.step === 'identifier' ? (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel} htmlFor="forgot-password-email">
                {t('auth.portal.emailLabel')}
              </label>
              <input
                id="forgot-password-email"
                className={styles.textInput}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={reset.email}
                disabled={reset.submitting}
                placeholder={t('auth.portal.emailPlaceholder')}
                onChange={(event) => reset.onChangeEmail(event.target.value)}
              />
            </div>
          ) : null}

          {reset.channel === 'phone' && reset.step === 'identifier' ? (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel} htmlFor="forgot-password-phone">
                {t('auth.portal.phoneOtpLabel')}
              </label>
              <div className={styles.phoneRow}>
                <span className={styles.phonePrefix} aria-hidden>
                  +972
                </span>
                <input
                  id="forgot-password-phone"
                  className={styles.phoneInputActive}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={reset.phone}
                  disabled={reset.submitting}
                  placeholder={t('auth.portal.phoneOtpPlaceholder')}
                  onChange={(event) => reset.onChangePhone(event.target.value)}
                />
              </div>
            </div>
          ) : null}

          {inCodeStep ? (
            <>
              <AuthOtpCodeField
                id="forgot-password-code"
                label={t('auth.portal.codeLabel')}
                value={reset.code}
                disabled={reset.submitting}
                placeholder={
                  reset.channel === 'email'
                    ? t('auth.portal.codePlaceholder')
                    : t('auth.portal.smsCodePlaceholder')
                }
                onChange={reset.onChangeCode}
              />
              <button type="button" className={styles.changeEmailLink} onClick={reset.onBackToIdentifier}>
                {reset.channel === 'email' ? t('auth.portal.changeEmail') : t('auth.portal.changePhone')}
              </button>
            </>
          ) : null}

          {inPasswordStep ? (
            <>
              <div className={styles.fieldBlock}>
                <label className={styles.fieldLabel} htmlFor="forgot-password-new">
                  {t('auth.forgotPassword.newPassword')}
                </label>
                <PasswordInputRow
                  id="forgot-password-new"
                  value={reset.newPassword}
                  onChange={reset.onChangeNewPassword}
                  inputClassName={styles.textInput}
                  toggleClassName={styles.passwordToggle}
                  autoComplete="new-password"
                  disabled={reset.submitting}
                  forceLtr
                />
              </div>
              <div className={styles.fieldBlock}>
                <label className={styles.fieldLabel} htmlFor="forgot-password-confirm">
                  {t('auth.forgotPassword.confirmPassword')}
                </label>
                <PasswordInputRow
                  id="forgot-password-confirm"
                  value={reset.confirmPassword}
                  onChange={reset.onChangeConfirmPassword}
                  inputClassName={styles.textInput}
                  toggleClassName={styles.passwordToggle}
                  autoComplete="new-password"
                  disabled={reset.submitting}
                  ariaInvalid={reset.passwordsMismatch}
                  ariaDescribedBy={reset.passwordsMismatch ? 'forgot-password-mismatch' : undefined}
                  forceLtr
                />
                {reset.passwordsMismatch ? (
                  <p id="forgot-password-mismatch" className={styles.friendlyError}>
                    {t('auth.forgotPassword.mismatch')}
                  </p>
                ) : null}
              </div>
              <button type="button" className={styles.changeEmailLink} onClick={reset.onBackToIdentifier}>
                {reset.channel === 'email' ? t('auth.portal.changeEmail') : t('auth.portal.changePhone')}
              </button>
            </>
          ) : null}

          {reset.errorFriendly ? (
            <div className={styles.failureBlock} role="alert">
              <p className={styles.friendlyError}>{reset.errorFriendly}</p>
            </div>
          ) : null}

          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={reset.submitting || reset.passwordsMismatch}
            aria-busy={reset.submitting}
          >
            {reset.submitting ? t('common.submitting') : submitLabel}
          </button>
        </form>
      )}
    </section>
  );
}
